import React from 'react';

interface AnalyticsSummary {
  total_screens: number;
  total_revenue: number;
  total_material_cost: number;
  total_labor_cost: number;
  total_overhead: number;
  total_true_cost: number;
  total_margin: number;
  avg_margin_percent: number;
  avg_cost_per_screen: number;
  avg_revenue_per_screen: number;
  rework_count: number;
  scrap_count: number;
}

interface HeadlineCardsProps {
  summary: AnalyticsSummary | null;
  loading: boolean;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatPercent = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

export function HeadlineCards({ summary, loading }: HeadlineCardsProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
            <div className="h-8 bg-gray-200 rounded w-32"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-700">
        No data available for the selected time period.
      </div>
    );
  }

  const cards = [
    {
      label: 'Total Screens',
      value: summary.total_screens.toLocaleString(),
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      label: 'Total Revenue',
      value: formatCurrency(summary.total_revenue),
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      label: 'Total Cost',
      value: formatCurrency(summary.total_true_cost),
      color: 'text-red-600',
      bgColor: 'bg-red-50',
    },
    {
      label: 'Total Margin',
      value: formatCurrency(summary.total_margin),
      color: summary.total_margin >= 0 ? 'text-green-600' : 'text-red-600',
      bgColor: summary.total_margin >= 0 ? 'bg-green-50' : 'bg-red-50',
    },
    {
      label: 'Avg Margin %',
      value: formatPercent(summary.avg_margin_percent),
      color: summary.avg_margin_percent >= 25 ? 'text-green-600' : summary.avg_margin_percent >= 15 ? 'text-yellow-600' : 'text-red-600',
      bgColor: summary.avg_margin_percent >= 25 ? 'bg-green-50' : summary.avg_margin_percent >= 15 ? 'bg-yellow-50' : 'bg-red-50',
    },
    {
      label: 'Avg Cost/Screen',
      value: formatCurrency(summary.avg_cost_per_screen),
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
    },
    {
      label: 'Rework Count',
      value: summary.rework_count.toString(),
      color: summary.rework_count > 0 ? 'text-orange-600' : 'text-green-600',
      bgColor: summary.rework_count > 0 ? 'bg-orange-50' : 'bg-green-50',
    },
    {
      label: 'Scrap Count',
      value: summary.scrap_count.toString(),
      color: summary.scrap_count > 0 ? 'text-red-600' : 'text-green-600',
      bgColor: summary.scrap_count > 0 ? 'bg-red-50' : 'bg-green-50',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {cards.map((card, index) => (
        <div key={index} className={`${card.bgColor} rounded-xl border border-gray-200 p-4`}>
          <p className="text-sm text-gray-600 mb-1">{card.label}</p>
          <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
        </div>
      ))}
    </div>
  );
}

export default HeadlineCards;
